import {
  DEFAULT_TILE_METERS,
  MAX_PAINTED_LAYERS,
  surfacePalette,
  type SurfaceLayer,
} from '@/terrain/TerrainSurface';
import { forgeTexture, importSwatch, type ForgedTexture } from '@/texture/TextureForge';
import { MAX_SWATCH_BYTES } from '@/texture/SwatchImage';
import { stockTextures, type StockTextureDefinition } from '@/texture/StockCatalog';
import { bundledSwatchUrl, instantiateStockTexture } from '@/texture/StockLibrary';
import { Dialog, dialogButton, dialogField, dialogSection, numberInput, textInput } from './Dialog';
import { clear, el } from './dom';
import { icon } from './Icons';

/**
 * Where a new ground material comes from — the three ways, in one dialog.
 *
 * **From the library** is the default, and it is first because it is the right answer most of the
 * time. Most gardens want bluestone, or bark, or decomposed granite; none of those should cost a
 * web search, an image call and a ten-second wait, and none of them should come out slightly
 * different every time somebody asks. The catalog is instant, free, and the same twice.
 *
 * **Describe it** is what the library cannot do: a phrase goes out, a grounded search comes back
 * with what the material really is, and an image model renders the swatch. **Upload one** is the
 * escape hatch below that, and it exists because the forge cannot be right about everything —
 * someone holding a photograph of the exact gravel on the exact job does not want a model's
 * impression of it, and a tool that made them argue with a search result to get their own picture in
 * would be an insulting one.
 *
 * The three share a dialog rather than sitting behind three buttons because they answer the same
 * question and differ only in where the pixels come from. The physical fields — tile size,
 * roughness, whether a plant can go in it — are the same in every case; the library and the forge
 * already know them and the upload asks.
 */

export interface TextureStudioDeps {
  /** The HUD root, so the dialog inherits the theme. */
  host: () => HTMLElement;
  /** The OpenAI key, or null. Null still works — the material is made offline, and it says so. */
  apiKey: () => string | null;
  /** Called after the palette changes, so the ground panel and the HUD re-read it. */
  onChange: () => void;
  /** Arm the brush with a material that has just been made. */
  onPick?: (layer: SurfaceLayer) => void;
  /** Injected for tests. */
  forgeImpl?: typeof forgeTexture;
  importImpl?: typeof importSwatch;
  libraryImpl?: typeof instantiateStockTexture;
}

type Mode = 'library' | 'describe' | 'upload';

export class TextureStudio {
  private readonly dialog: Dialog;
  private readonly body: HTMLElement;
  private mode: Mode = 'library';
  private busy = false;

  /* Library */
  private query = '';
  private pick: string | null = null;
  /** Held so a keystroke in the search box refills the grid without re-rendering the input. */
  private grid: HTMLElement | null = null;

  /* Describe */
  private request = '';
  /* Upload */
  private file: File | null = null;
  private uploadName = '';
  private tileMeters = DEFAULT_TILE_METERS;
  private roughness = 0.85;
  private plantable = true;
  private alreadyTileable = false;

  private status: { kind: 'error' | 'note' | 'working'; lines: string[] } | null = null;

  constructor(private readonly deps: TextureStudioDeps) {
    this.dialog = new Dialog({
      host: deps.host(),
      title: 'New ground material',
      icon: 'swatch',
      subtitle: '',
      onClose: () => this.reset(),
    });
    this.body = this.dialog.body;
  }

  open(): void {
    this.reset();
    this.render();
    this.dialog.show();
  }

  get isOpen(): boolean {
    return this.dialog.isOpen;
  }

  private reset(): void {
    this.busy = false;
    this.mode = 'library';
    this.query = '';
    this.pick = null;
    this.grid = null;
    this.request = '';
    this.file = null;
    this.uploadName = '';
    this.tileMeters = DEFAULT_TILE_METERS;
    this.roughness = 0.85;
    this.plantable = true;
    this.alreadyTileable = false;
    this.status = null;
  }

  /* ---------------------------------- Render ---------------------------------- */

  private render(): void {
    const free = surfacePalette.freeSlots;
    this.dialog.setSubtitle(
      free > 0
        ? `${free} of ${MAX_PAINTED_LAYERS} painted materials free on this site`
        : 'Every painted slot is taken — retire one in the ground panel first',
    );

    clear(this.body);
    this.body.appendChild(this.buildModeSwitch());
    this.body.appendChild(
      this.mode === 'library'
        ? this.buildLibrary()
        : this.mode === 'describe'
          ? this.buildDescribe()
          : this.buildUpload(),
    );
    const status = this.buildStatus();
    if (status) this.body.appendChild(status);

    this.dialog.setActions(
      dialogButton({ label: 'Cancel', onClick: () => this.dialog.close() }),
      dialogButton({
        label: this.busy ? 'Working…' : this.mode === 'describe' ? 'Make it' : 'Add material',
        icon: this.mode === 'describe' ? 'sparkle' : this.mode === 'library' ? 'swatch' : 'upload',
        primary: true,
        disabled: this.busy || free <= 0 || !this.canSubmit(),
        onClick: () => void this.submit(),
      }),
    );
  }

  private buildModeSwitch(): HTMLElement {
    const row = el('div', {
      class: 'lg-segment',
      attrs: { role: 'group', 'aria-label': 'How to make the material' },
    });
    const items: readonly { id: Mode; label: string }[] = [
      { id: 'library', label: 'Library' },
      { id: 'describe', label: 'Describe it' },
      { id: 'upload', label: 'Upload a swatch' },
    ];
    for (const item of items) {
      row.appendChild(
        el('button', {
          class: `lg-segment__item${item.id === this.mode ? ' is-active' : ''}`,
          text: item.label,
          attrs: { type: 'button', 'aria-pressed': item.id === this.mode, 'data-autofocus': 'skip' },
          on: {
            click: () => {
              if (this.busy) return;
              this.mode = item.id;
              this.status = null;
              this.render();
            },
          },
        }),
      );
    }
    return row;
  }

  /**
   * The catalog, searchable, as a grid of real swatches.
   *
   * The chips are the bundler's own asset URLs rather than inlined data — the picker can show every
   * material in the library without any of them being copied into the project, and only the one
   * actually taken gets fetched and inlined. That is the whole point of the catalog/palette split,
   * and it is visible right here: browsing costs nothing.
   */
  private buildLibrary(): HTMLElement {
    const search = textInput({
      value: this.query,
      placeholder: 'bluestone, mulch, driveway…',
      ariaLabel: 'Search the material library',
      onInput: (value) => {
        this.query = value;
        // Only the grid is refilled. Re-rendering the whole body would rebuild this input and take
        // the caret with it, which makes a search box impossible to type in.
        this.renderLibraryGrid();
      },
    });
    search.disabled = this.busy;

    this.grid = el('div', {
      class: 'lg-library__grid',
      attrs: { role: 'listbox', 'aria-label': 'Ground materials' },
    });

    const group = el(
      'div',
      { class: 'lg-dialog__group lg-library' },
      dialogField('Search', search),
      this.grid,
    );
    this.renderLibraryGrid();
    return group;
  }

  private renderLibraryGrid(): void {
    const grid = this.grid;
    if (!grid) return;
    clear(grid);

    const matches = stockTextures.search(this.query);
    if (matches.length === 0) {
      grid.appendChild(
        el('p', {
          class: 'lg-library__empty',
          text: `Nothing in the library matches “${this.query.trim()}”. Try “Describe it” — the forge will make it.`,
        }),
      );
      return;
    }
    for (const texture of matches) grid.appendChild(this.libraryCard(texture));
  }

  private libraryCard(texture: StockTextureDefinition): HTMLElement {
    const url = bundledSwatchUrl(texture.file);
    const installed = surfacePalette.has(texture.id);
    const selected = this.pick === texture.id;

    const card = el(
      'button',
      {
        class: `lg-library__item${selected ? ' is-active' : ''}`,
        title: [texture.hint, texture.grounding].filter(Boolean).join('\n'),
        attrs: {
          type: 'button',
          role: 'option',
          'aria-selected': selected,
          'data-autofocus': 'skip',
        },
        on: {
          click: () => {
            if (this.busy) return;
            this.pick = texture.id;
            this.status = null;
            this.renderLibraryGrid();
            this.syncSubmitEnabled();
          },
        },
      },
      el('span', {
        class: 'lg-library__swatch',
        style: {
          backgroundColor: texture.color,
          // Two tiles across the chip. One is a crop of a material rather than a picture of it;
          // four — the ground panel's 16px setting — is too fine to read at this size.
          ...(url ? { backgroundImage: `url(${url})`, backgroundSize: '50% 50%' } : {}),
        },
      }),
      el('span', { class: 'lg-library__name', text: texture.name }),
      el('span', {
        class: 'lg-library__meta',
        text: installed
          ? 'on this site'
          : texture.plantable
            ? `tiles ${formatMeters(texture.tileMeters)}`
            : 'no planting',
      }),
    );
    card.disabled = this.busy;
    return card;
  }

  private buildDescribe(): HTMLElement {
    const input = textInput({
      value: this.request,
      placeholder: 'red gravel',
      ariaLabel: 'Describe the material',
      onInput: (value) => {
        this.request = value;
        this.syncSubmitEnabled();
      },
      onSubmit: () => void this.submit(),
    });
    input.disabled = this.busy;

    const grounded = this.deps.apiKey() !== null;
    return el(
      'div',
      { class: 'lg-dialog__group' },
      dialogField(
        'Material',
        input,
        grounded
          ? 'Searched for before anything is drawn, so "red gravel" comes back as the crushed lava rock a supplier sells rather than the colour red.'
          : 'No API key set, so the colour is read out of the words and the swatch is drawn rather than photographed. Add a key in the enhance panel for a researched material.',
      ),
      el('p', {
        class: 'lg-dialog__hint',
        text: 'Be specific where it matters: "weathered bluestone pavers", "cocoa shell mulch", "pea shingle 10 mm".',
      }),
    );
  }

  private buildUpload(): HTMLElement {
    const picker = el('input', {
      class: 'lg-input',
      attrs: {
        type: 'file',
        accept: 'image/png,image/jpeg,image/webp',
        'aria-label': 'Swatch image',
      },
      on: {
        change: (event) => {
          const chosen = (event.target as HTMLInputElement).files?.[0] ?? null;
          this.file = chosen;
          if (chosen && this.uploadName.trim().length === 0) {
            this.uploadName = fileStem(chosen.name);
          }
          this.status = null;
          this.render();
        },
      },
    });
    picker.disabled = this.busy;

    const name = textInput({
      value: this.uploadName,
      placeholder: 'Client’s gravel',
      ariaLabel: 'Material name',
      onInput: (value) => {
        this.uploadName = value;
        this.syncSubmitEnabled();
      },
    });
    name.disabled = this.busy;

    return el(
      'div',
      { class: 'lg-dialog__group' },
      dialogField(
        'Image',
        picker,
        `A top-down photograph of the ground works best. Square or not — it is centre-cropped and resampled. Up to ${Math.round(MAX_SWATCH_BYTES / 1024 / 1024)} MB.`,
      ),
      dialogField('Name', name),
      dialogSection('How it sits on the ground'),
      dialogField(
        'Tile size',
        numberInput({
          value: this.tileMeters,
          min: 0.1,
          max: 20,
          step: 0.1,
          ariaLabel: 'Metres one tile covers',
          onChange: (value) => {
            this.tileMeters = value;
          },
        }),
        'Metres of ground one copy of the image covers. Get this wrong and the grain is the wrong size — a photo of a metre of gravel is 1.0.',
      ),
      dialogField(
        'Roughness',
        numberInput({
          value: this.roughness,
          min: 0,
          max: 1,
          step: 0.05,
          ariaLabel: 'Roughness',
          onChange: (value) => {
            this.roughness = value;
          },
        }),
        'How the sun catches it. Water 0.05, polished stone 0.3, asphalt 0.55, gravel 0.9, bark 0.98.',
      ),
      this.checkbox('Plants can be placed here', this.plantable, (value) => {
        this.plantable = value;
      }),
      this.checkbox(
        'This image already tiles seamlessly',
        this.alreadyTileable,
        (value) => {
          this.alreadyTileable = value;
        },
        'Leave this off for a photograph. On, the borders are used as-is; off, they are cross-faded so the texture repeats without a seam.',
      ),
    );
  }

  private checkbox(
    label: string,
    checked: boolean,
    onChange: (value: boolean) => void,
    hint?: string,
  ): HTMLElement {
    const input = el('input', {
      attrs: { type: 'checkbox', 'aria-label': label },
      on: { change: (event) => onChange((event.target as HTMLInputElement).checked) },
    });
    input.checked = checked;
    input.disabled = this.busy;
    return el(
      'label',
      { class: 'lg-dialog__check' },
      input,
      el('span', { text: label }),
      hint !== undefined ? el('span', { class: 'lg-dialog__hint', text: hint }) : null,
    );
  }

  private buildStatus(): HTMLElement | null {
    if (!this.status) return null;
    const { kind, lines } = this.status;
    return el(
      'div',
      { class: `lg-dialog__status lg-dialog__status--${kind}` },
      el('span', {
        class: 'lg-dialog__status-icon',
        html: icon(kind === 'error' ? 'warning' : kind === 'working' ? 'sparkle' : 'info', 15),
      }),
      el('div', {}, ...lines.map((line) => el('p', { text: line }))),
    );
  }

  private syncSubmitEnabled(): void {
    const primary = this.dialog.root.querySelector<HTMLButtonElement>('.lg-button--primary');
    if (primary) primary.disabled = this.busy || surfacePalette.freeSlots <= 0 || !this.canSubmit();
  }

  private canSubmit(): boolean {
    if (this.mode === 'library') return this.pick !== null;
    return this.mode === 'describe'
      ? this.request.trim().length > 0
      : this.file !== null && this.uploadName.trim().length > 0;
  }

  /* ---------------------------------- Submit ---------------------------------- */

  private async submit(): Promise<void> {
    if (this.busy || !this.canSubmit()) return;
    this.busy = true;
    this.status = {
      kind: 'working',
      lines:
        this.mode === 'library'
          ? ['Adding it to this site.']
          : this.mode === 'describe'
            ? this.deps.apiKey()
              ? ['Researching the material, then rendering its swatch. This takes a few seconds.']
              : ['Drawing the material from its description.']
            : ['Reading the image and closing its seams.'],
    };
    this.render();

    try {
      const layer =
        this.mode === 'library'
          ? await this.runLibrary()
          : this.mode === 'describe'
            ? (await this.runForge()).layer
            : await this.runImport();
      this.deps.onChange();
      // Arming the brush with what was just made: nobody makes a material in order to look at it.
      this.deps.onPick?.(layer);
      this.dialog.close();
    } catch (error) {
      this.busy = false;
      this.status = { kind: 'error', lines: [describe(error)] };
      this.render();
    }
  }

  private async runLibrary(): Promise<SurfaceLayer> {
    const id = this.pick;
    if (id === null) throw new Error('Choose a material first.');
    return (this.deps.libraryImpl ?? instantiateStockTexture)(id);
  }

  private async runForge(): Promise<ForgedTexture> {
    const forged = await (this.deps.forgeImpl ?? forgeTexture)({
      request: this.request,
      apiKey: this.deps.apiKey(),
    });
    return forged;
  }

  private async runImport(): Promise<SurfaceLayer> {
    const file = this.file;
    if (!file) throw new Error('Choose an image first.');
    return (this.deps.importImpl ?? importSwatch)({
      file,
      name: this.uploadName,
      sourceLabel: file.name,
      tileMeters: this.tileMeters,
      roughness: this.roughness,
      plantable: this.plantable,
      alreadyTileable: this.alreadyTileable,
    });
  }

  dispose(): void {
    this.dialog.dispose();
  }
}

/* ------------------------------ Material details ------------------------------ */

/**
 * The provenance line shown under a custom material in the ground panel.
 *
 * Worth the row it costs. Three weeks after a site is built, "Red Lava Rock" is a name; what makes
 * it checkable is that it was made from the phrase "red gravel" and that the search found scoria
 * chips. A material whose grounding is empty says so, which is the whole point — an invented colour
 * and a researched one must not look alike.
 */
export function describeLayerOrigin(layer: SurfaceLayer): string | null {
  const origin = layer.origin;
  if (!origin) return null;
  // `grounding` is optional on the type even though every catalog entry currently carries one — an
  // older document, or a hand-edited one, may not.
  if (origin.kind === 'stock') {
    return origin.grounding ? `From the library. ${origin.grounding}` : 'From the library.';
  }
  if (origin.kind === 'uploaded') return `Uploaded from ${origin.request}.`;
  return origin.grounding
    ? `Made from “${origin.request}”. ${origin.grounding}`
    : `Made from “${origin.request}” without a grounded search — check the colour.`;
}

/** "1.5 m", "80 cm" — whichever reads as a size rather than as a number. */
function formatMeters(meters: number): string {
  return meters < 1 ? `${Math.round(meters * 100)} cm` : `${meters.toFixed(1).replace(/\.0$/, '')} m`;
}

function fileStem(name: string): string {
  return name
    .replace(/\.[^.]+$/, '')
    .replace(/[-_]+/g, ' ')
    .trim()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
}

function describe(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'Something went wrong making that material.';
}
